import InfiniteScrollObserver from "@component/InfiniteScrollObserver";
import useDebounce from "@hooks/useDebounce";
import { ListResponseType } from "@shared/types";

import { Select, SelectProps } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type ListParams = { name?: string; limit: number; offset: number; [key: string]: any };

type Props = {
  fetchListApi: (params: ListParams) => Promise<ListResponseType<any>>;
  optionsKey?: { label: string; value: string };
  limit?: number;
  initialOptions?: { label: string; value: number }[];
} & SelectProps;

const DEFAULT_OPTIONS_KEY = { label: "name", value: "id" };

const EMPTY_LIST_RESULT: ListResponseType<any> = { count: 0, next: null, previous: null, results: [] };

/**
 * 依分頁 API 動態載入選項、支援關鍵字搜尋、捲動載入下一頁的 antd Select 封裝元件。
 * 除下列 props 外，其餘 SelectProps（value、onChange、mode、placeholder、defaultValue...）皆會直接透傳給 antd Select。
 * @param fetchListApi - 取得列表資料的 API，搜尋時會帶入 name 參數
 * @param optionsKey - 選項的 label（顯示文字）與 value（回傳值）對應的欄位名稱，預設 { label: "name", value: "id" }
 * @param limit - 一次取得的項目數量，預設 10
 * @param initialOptions - 掛載時先塞入的選項，用於已知 value 對應的 label 時提前補上，避免該筆資料還沒被分頁抓到前顯示成原始 value
 */
const InfiniteScrollSelect = (props: Props) => {
  const { fetchListApi, optionsKey = DEFAULT_OPTIONS_KEY, limit = 10, initialOptions = [], ...rest } = props;
  const [isFetching, setIsFetching] = useState(false);
  const [baseListResult, setBaseListResult] = useState<ListResponseType<any>>(EMPTY_LIST_RESULT);
  const [searchListResult, setSearchListResult] = useState<ListResponseType<any> | undefined>();
  const offset = useRef(0);
  const { label: optionLabel, value: optionValue } = optionsKey;

  const fetchList = useCallback(
    async (params: ListParams): Promise<ListResponseType<any> | undefined> => {
      setIsFetching(true);
      try {
        return await fetchListApi(params);
      } catch (error) {
        // API 錯誤已由 CoreAPI interceptor 統一處理
        return undefined;
      } finally {
        setIsFetching(false);
      }
    },
    [fetchListApi],
  );

  const fetchBaseList = useCallback(
    async (params: ListParams, loadMore?: boolean) => {
      const response = await fetchList(params);
      if (!response) return;
      setBaseListResult((prevState) => ({
        ...response,
        results: loadMore ? [...prevState.results, ...response.results] : response.results,
      }));
    },
    [fetchList],
  );

  const handleOnKeywordSearch = useDebounce(async (keyword: string) => {
    if (!keyword) {
      setSearchListResult(undefined);
      return;
    }

    const response = await fetchList({ name: keyword, offset: 0, limit: 300 });
    if (response) setSearchListResult(response);
  }, 300);

  const handleOnLoadMore = useCallback(() => {
    const nextOffset = offset.current + limit;
    fetchBaseList({ limit, offset: nextOffset }, true);
    offset.current = nextOffset;
  }, [fetchBaseList, limit]);

  const handleOnDropdownVisibleChange = useCallback((open: boolean) => {
    // 關閉時清空搜尋狀態，重新打開會顯示 baseListResult 快取的完整清單
    if (!open) {
      setSearchListResult(undefined);
    }
  }, []);

  const activeListResult = searchListResult ?? baseListResult;

  const options = useMemo<{ label: React.ReactNode; value: any; disabled?: boolean }[]>(() => {
    const { next, results } = activeListResult;
    const items: { label: React.ReactNode; value: any }[] = results.map((item) => ({
      label: item[optionLabel],
      value: item[optionValue],
    }));

    // 保留 initialOptions 裡尚未被這一批結果包含的項目,避免已知 value 對應的 label 被蓋掉
    const seededOptions: { label: React.ReactNode; value: any }[] = initialOptions.filter(
      (seed) => !items.some((item) => item.value === seed.value),
    );
    const merged: { label: React.ReactNode; value: any; disabled?: boolean }[] = [...seededOptions, ...items];

    if (next) {
      merged.push({
        value: -1,
        disabled: true,
        label: (
          <>
            loading...
            <InfiniteScrollObserver callback={handleOnLoadMore} />
          </>
        ),
      });
    }
    return merged;
  }, [handleOnLoadMore, optionLabel, activeListResult, optionValue, initialOptions]);

  useEffect(() => {
    fetchBaseList({ limit, offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Select
      showSearch
      filterOption={false}
      onSearch={handleOnKeywordSearch}
      onDropdownVisibleChange={handleOnDropdownVisibleChange}
      loading={isFetching}
      options={options}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
};

export default InfiniteScrollSelect;
